import { assign } from 'xstate';

import {
  addBroadcastEventsListener,
  broadcastLogInEvent,
  broadcastLogOutEvent,
  removeBroadcastEventsListener,
  resetCurrentCustomerData,
  setCurrentCustomerData,
} from './actions';
import { AuthModel } from './AuthMachine.model';
import { fetchCustomer } from './services';

import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const createAuthMachine = (options: CreateAuthMachineOptions) => {
  return AuthModel.createMachine(
    {
      predictableActionArguments: true, // https://xstate.js.org/docs/guides/actions.html
      context: AuthModel.initialContext,
      id: 'auth-machine',
      initial: 'idle',
      states: {
        idle: {
          on: {
            INIT_GUEST_SESSION: {
              target: '#auth-machine.session.loggedOut',
              actions: 'saveCsrfTokenToStorage',
            },
            INIT_LOGGED_IN_SESSION: {
              target: '#auth-machine.fetching.customer',
              actions: 'saveCsrfTokenToStorage',
            },
          },
        },
        fetching: {
          states: {
            customer: {
              id: 'fetching.customer',
              invoke: {
                id: 'fetchCustomer',
                src: async () => fetchCustomer(options),
                onDone: {
                  target: '#auth-machine.session.loggedIn',
                  actions: 'setCurrentCustomerData',
                },
                onError: {
                  target: '#auth-machine.session.loggedOut',
                },
              },
            },
          },
        },
        session: {
          entry: 'markFirstAuthAttemptDone',
          states: {
            loggedOut: {
              on: {
                LOGIN: {
                  target: '#auth-machine.fetching.customer',
                  actions: [
                    'saveCsrfTokenToStorage',
                    'broadcastLogInEvent',
                    'capturePostLoginRedirectHref',
                  ],
                },
              },
            },
            loggedIn: {
              on: {
                LOGOUT: {
                  target: 'loggedOut',
                  actions: [
                    'saveCsrfTokenToStorage',
                    'broadcastLogOutEvent',
                    'resetCurrentCustomerData',
                  ],
                },
              },
            },
          },
        },
      },
      entry: 'addBroadcastEventsListener',
      exit: 'removeBroadcastEventsListener',
      on: {
        UPDATE_CSRF_TOKEN: {
          actions: 'saveCsrfTokenToStorage',
        },
        CLEAR_POST_LOGIN_REDIRECT_HREF: {
          actions: 'clearPostLoginRedirectHref',
        },
      },
    },
    {
      actions: {
        broadcastLogInEvent,
        broadcastLogOutEvent,
        addBroadcastEventsListener,
        removeBroadcastEventsListener,
        setCurrentCustomerData,
        resetCurrentCustomerData,
        capturePostLoginRedirectHref: assign({
          postLoginRedirectHref: (_context, event) =>
            event.type === 'LOGIN' ? event.postLoginRedirectHref : undefined,
        }),
        clearPostLoginRedirectHref: assign({
          postLoginRedirectHref: (_context) => undefined,
        }),
        markFirstAuthAttemptDone: assign({
          isFirstAuthAttempt: (_context) => false,
        }),
      },
    },
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export type CreateAuthMachineOptions = {
  client: Client;
};
